import React from 'react';
import { get } from 'lodash-es';
import { createSelector } from 'reselect';

import Global from '../../../store/global-selectors';

import { OtherShippingOptionsService } from '../../../services/other-shipping-options/other-shipping-options-service';
import { FulfillmentPlanService } from '../../../services/fulfillment-plan-service';
import * as ShipmentDetailsHelper from '../../../components/shipment-details/shipment-details-helper';
import { convertAndSumWeightValues } from '../../../services/unit-services/weights/weight-convert-and-sum-service';
import { convertDimensions } from '../../../services/unit-services/dimensions/dimension-convert-service';
import { ShipmentHelperService } from '../../../services/shipment-helper-service';
import { MultiPackage } from '../components/multi-package';

export const getShipmentInformation = (state, { fulfillmentPlanId }) => {
  const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || {};
  const salesOrders = Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, { fulfillmentPlanIds: [fulfillmentPlanId] });
  const fulfillments = Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, { fulfillmentPlanId });
  const fulfillment = fulfillments.find((f) => !f.isLabelVoided);
  return {
    fulfillmentPlan,
    fulfillment,
    salesOrders,
  };
};

export const getPackagingDetails = (state, shipment, fulfillmentPlan, fulfillment, dimensionsUnit, weightUnit) => {
  const { shipFromId, packages } = shipment;
  const shipFrom = get(Global.selectSellerShipFromsById(state, { shipFromId }), 'name') || get(fulfillment, 'shipFrom.name');
  if (FulfillmentPlanService.isExternal(fulfillmentPlan)) {
    return ShipmentDetailsHelper.thirdPartyFulfillmentPackagingDetails(fulfillmentPlan, fulfillment, shipFrom);
  }
  const packageType = packages && packages.length > 1 ? <MultiPackage /> : get(Global.selectSellerPackageType(state, shipment), 'name');
  if (!packageType) {
    return {
      shipFrom,
    };
  }
  const weights = (packages || []).map((p) => p.weight);
  const totalWeight = convertAndSumWeightValues(weights, weightUnit);
  const pkg = packages[0] || {};
  const dimensions = convertDimensions(pkg.dimensions, dimensionsUnit);
  return {
    weight: ShipmentDetailsHelper.formatWeight(totalWeight, weightUnit),
    dimensionsWithUnit: ShipmentDetailsHelper.formatDimensions(dimensions),
    shipFrom,
    packageType,
    dimensionsUnit,
  };
};

const shipmentSelector = createSelector(
  ({ fulfillmentPlan }) => fulfillmentPlan,
  ({ services }) => services,
  ({ carriers }) => carriers,
  ({ salesOrders }) => salesOrders,
  ({ fulfillment }) => fulfillment,
  (fulfillmentPlan, services, carriers, salesOrders, fulfillment) => {
    const model = {
      ...fulfillmentPlan,
      ...fulfillment,
      packages: fulfillment?.packages?.length ? fulfillment.packages : fulfillmentPlan.packages,
    };
    const { serviceId } = model;
    const selectedService = services[serviceId];
    const requestedService = salesOrders.map((so) => get(so, 'requestedService')).join(',');
    const trackingInformation = model.trackingInformation;
    const carrierId = model.carrierId;
    const carrier = carriers.find((car) => car.carrierId === carrierId);
    const shippingCost = model.rateSummary?.rate?.shippingCost;
    const rateSummaryTotalCost = model.rateSummary?.rate?.totalCost;
    const pickupDate = model.rateSummary?.rate?.estimatedPickup;
    const deliveryDate = model.rateSummary?.rate?.estimatedDelivery;
    const driverTip = model.rateSummary?.rate?.driverTip;
    const delegationDate = fulfillment?.createDateTime;
    const totalCost = ShipmentHelperService.getTotalCost(fulfillment, rateSummaryTotalCost) || rateSummaryTotalCost;
    const shipment = {
      ...model,
      requestedService,
      selectedService,
      carrier,
      totalCost,
      confirmation: OtherShippingOptionsService.getConfirmationText(model, carriers, selectedService),
      trackingInformation,
      driverTip,
      pickupDate,
      deliveryDate,
      shippingCost,
      delegationDate,
    };
    return shipment;
  },
);

export const buildShipmentInformation = (state, fulfillmentPlan, fulfillment, salesOrders) => {
  const serviceReferenceLookup = Global.selectReferenceDatagetServiceReferenceLookup(state);
  const carriers = Global.selectReferenceDatagetAllTheCarriers(state);

  return shipmentSelector({
    fulfillmentPlan,
    services: serviceReferenceLookup,
    carriers,
    salesOrders,
    fulfillment,
  });
};
